import { createApi } from '@reduxjs/toolkit/dist/query/react'
import baseQueryWithOnlineCourse from '../../app/baseQueryWithOnlineCourse'
import {
    TestAnalysisState,
    VideoAnalysisState,
    TopicState,
    UserTopicState,
} from '../types'

interface getStudentTopicSectionAnalysisParams {
    courseSubjectId: number
    studentId: number
}

interface getFilteredTopicsParams {
    language: string
    filter: string
}

export const topicApiSlice = createApi({
    reducerPath: 'topicApi',
    baseQuery: baseQueryWithOnlineCourse,
    tagTypes: ['Topic', 'TopicVideoLesson', 'TopicTest'],
    endpoints: (build) => ({
        getTopicsBySubject: build.query<TopicState[], number>({
            query: (id) => ({
                url: `topic/by-subject/${id}`,
            }),
            providesTags: (result) =>
                result
                    ? [
                          ...result.map(({ id }) => ({
                              type: 'Topic' as const,
                              id,
                          })),
                          { type: 'Topic', id: 'LIST' },
                      ]
                    : [{ type: 'Topic', id: 'LIST' }],
            transformResponse: (response: any) => response.data,
        }),

        createTopic: build.mutation<void, TopicState>({
            query: (topic) => ({
                url: 'topic',
                method: 'POST',
                body: topic,
            }),
            invalidatesTags: [{ type: 'Topic', id: 'LIST' }],
        }),

        updateTopic: build.mutation<void, TopicState>({
            query: (topic) => ({
                url: `/topic/${topic.id}`,
                method: 'PUT',
                body: topic,
            }),
            invalidatesTags: [{ type: 'Topic', id: 'LIST' }],
        }),

        deleteTopic: build.mutation<{ success: boolean; id: number }, number>({
            query: (id) => ({
                url: `/topic/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, id) => [{ type: 'Topic', id }],
        }),

        getStudentTopicsBySubject: build.query<UserTopicState[], number>({
            query: (userSubjectEnrollId) => ({
                url: `user-topic/${userSubjectEnrollId}`,
            }),
            providesTags: (result) =>
                result
                    ? [
                          ...result.map(({ courseTopicId }) => ({
                              type: 'Topic' as const,
                              courseTopicId,
                          })),
                          { type: 'Topic', id: 'LIST' },
                      ]
                    : [{ type: 'Topic', id: 'LIST' }],
            transformResponse: (response: any) => response.data,
        }),

        getTeacherTopicsBySubject: build.query<UserTopicState[], number>({
            query: (courseSubjectId) => ({
                url: `user-topic/teacher/${courseSubjectId}`,
            }),
            providesTags: (result) =>
                result
                    ? [
                          ...result.map(({ courseTopicId }) => ({
                              type: 'Topic' as const,
                              courseTopicId,
                          })),
                          { type: 'Topic', id: 'LIST' },
                      ]
                    : [{ type: 'Topic', id: 'LIST' }],
            transformResponse: (response: any) => response.data,
        }),

        getStudentTopicVideoAnalysis: build.query<
            VideoAnalysisState[],
            getStudentTopicSectionAnalysisParams
        >({
            query: ({ courseSubjectId, studentId }) => ({
                url: `watched-video/student-analysis/${courseSubjectId}`,
                params: {
                    studentId,
                },
            }),
            providesTags: (result) =>
                result
                    ? [
                          ...result.map(({ courseTopicId }) => ({
                              type: 'TopicVideoLesson' as const,
                              courseTopicId,
                          })),
                          { type: 'TopicVideoLesson', id: 'LIST' },
                      ]
                    : [{ type: 'TopicVideoLesson', id: 'LIST' }],
            transformResponse: (response: any) => response.data,
        }),

        getStudentTopicTestAnalysis: build.query<
            TestAnalysisState[],
            getStudentTopicSectionAnalysisParams
        >({
            query: ({ courseSubjectId, studentId }) => ({
                url: `topic-test-result/student-analysis/${courseSubjectId}`,
                params: {
                    studentId,
                },
            }),
            providesTags: (result) =>
                result
                    ? [
                          ...result.map(({ courseTopicId }) => ({
                              type: 'TopicTest' as const,
                              courseTopicId,
                          })),
                          { type: 'TopicTest', id: 'LIST' },
                      ]
                    : [{ type: 'TopicTest', id: 'LIST' }],
            transformResponse: (response: any) => response.data,
        }),

        getFilteredTopics: build.query<any, getFilteredTopicsParams>({
            query: ({ language, filter }) => ({
                url: 'purchase/search',
                params: {
                    language,
                    filter,
                },
            }),
            // providesTags: (result) =>
            //     result
            //         ? [
            //               ...result.map(({ courseTopicId }) => ({
            //                   type: 'TopicTest' as const,
            //                   courseTopicId,
            //               })),
            //               { type: 'TopicTest', id: 'LIST' },
            //           ]
            //         : [{ type: 'TopicTest', id: 'LIST' }],
            transformResponse: (response: any) => response.data,
        }),
    }),
})
